import React, { type FC } from 'react';
import { cn } from '../../utils/cn';
import type { CrmStageProjection } from '../../types/crmStage.types';
import {
  buildCrmStageDisplayRows,
  crmStageAccessibleSummary,
  type CrmStageDisplayRow,
} from '../../utils/crmStageDisplay';

interface CrmStageDisplayProps {
  stages?: readonly CrmStageProjection[] | null;
  loading?: boolean;
  compact?: boolean;
  className?: string;
}

const StageRow: FC<{ row: CrmStageDisplayRow; compact: boolean }> = ({ row, compact }) => (
  <span className={cn('flex min-w-0 items-center gap-1.5', compact && 'max-w-[220px]')}>
    {row.state === 'loading' ? (
      <>
        {row.providerLabel && (
          <span className={compact ? 'truncate text-xs' : 'break-words text-sm'}>
            {row.providerLabel} ·
          </span>
        )}
        <span className="block h-4 w-16 animate-pulse rounded bg-muted" aria-hidden="true" />
      </>
    ) : (
      <span
        className={cn(
          'min-w-0 text-foreground',
          compact ? 'truncate text-xs' : 'break-words text-sm',
          row.state === 'never_synced' && 'text-muted-foreground'
        )}
        title={row.text}
      >
        {row.text}
      </span>
    )}
    {row.statusLabel && (
      <span
        className={cn(
          'shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none',
          row.state === 'stale'
            ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
            : 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
        )}
      >
        {row.statusLabel}
      </span>
    )}
  </span>
);

export const CrmStageDisplay: FC<CrmStageDisplayProps> = ({
  stages,
  loading = false,
  compact = false,
  className,
}) => {
  if (loading) {
    return (
      <span
        className={cn('block h-4 w-24 animate-pulse rounded bg-muted', className)}
        aria-label="Loading CRM stage"
      />
    );
  }

  const rows = buildCrmStageDisplayRows(stages);
  const summary = crmStageAccessibleSummary(rows);

  if (compact) {
    const first = rows[0];
    return (
      <span className={cn('flex min-w-0 items-center gap-1.5', className)} aria-label={summary}>
        <StageRow row={first} compact />
        {rows.length > 1 && (
          <span
            className="shrink-0 whitespace-nowrap text-[11px] font-medium text-muted-foreground"
            title={summary}
          >
            +{rows.length - 1} more
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={cn('flex min-w-0 flex-col gap-1.5', className)} aria-label={summary}>
      {rows.map((row) => (
        <StageRow key={row.key} row={row} compact={false} />
      ))}
    </span>
  );
};
